<template>
  <div class="translate-wrapper">
    <Loader :isVisible="isLoading" />
    <div
      v-if="hasChanges && shouldShowWarning"
      class="warning-message"
    >
      <inline-svg
        :src="iconWarning"
        class="warning-icon"
      ></inline-svg>
      Changes won’t be saved unless you click Apply.
    </div>
    <div
      v-if="formData.isInited && isNeedTitleImg"
      class="wrapper-small"
    >
      <CollectionImageUploader
        :source="getImageSource('collection-titleImage')"
        :submitEventName="imageSubmitEventName"
        :submitParams="imageSubmitParams"
        @handle-add-item-with-changes="handleAddItemWithChanges"
        @handle-remove-item-with-changes="handleRemoveItemWithChanges"
        @handle-item-in-progress="handleItemInProgress"
        @handle-reload-item="handleReloadItem"
      />
    </div>
    <div
      v-if="formData.isInited"
      class="wrapper-small"
    >
      <CollectionImageUploader
        :source="getImageSource('collection-posterImage')"
        :submitEventName="imageSubmitEventName"
        :submitParams="imageSubmitParams"
        @handle-add-item-with-changes="handleAddItemWithChanges"
        @handle-remove-item-with-changes="handleRemoveItemWithChanges"
        @handle-item-in-progress="handleItemInProgress"
        @handle-reload-item="handleReloadItem"
      />
    </div>
    <div
      v-if="formData.isInited"
      class="wrapper-small"
    >
      <CollectionImageUploader
        :source="getImageSource('collection-portraitImage')"
        :submitEventName="imageSubmitEventName"
        :submitParams="imageSubmitParams"
        @handle-add-item-with-changes="handleAddItemWithChanges"
        @handle-remove-item-with-changes="handleRemoveItemWithChanges"
        @handle-item-in-progress="handleItemInProgress"
        @handle-reload-item="handleReloadItem"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import BaseForm from '../BaseForm.vue';
import iconChanges from '../../../assets/icons/icon-localization-changes.svg';
import CollectionImageUploader from '../../common/PosterImage/CollectionImageUploader.vue';

import {
  PATCH_LANGUAGE_FOLDER_REQUEST,
  GET_LANGUAGE_FOLDER_REQUEST,
  INIT_FORM_CHANGES,
  SET_FORM_CHANGES,
} from '../../../store/actions/localization/localizationActions';
import Loader from '../../common/Loader.vue';
import iconWarning from '../../../assets/icons/icon-warning-localization.svg';

export default {
  name: 'CollectionImagesTranslateForm',
  components: {
    Loader,
    CollectionImageUploader,
  },
  extends: BaseForm,
  props: {
    metadata: {
      type: Object,
      default: () => {},
    },
    lang: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: Boolean,
      default: false,
    },
    enabledLanguages: {
      type: Array,
      default: () => [],
    },
    changedLanguage: {
      type: Function,
      default: () => {},
    },
    type: {
      type: String,
      default: '',
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    setHasChanges: {
      type: Function,
      default: () => {},
    },
    shouldShowWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      itemsWithChanges: [],
      iconWarning,
      iconChanges,
      imageSubmitEventName: PATCH_LANGUAGE_FOLDER_REQUEST,
      titleImageError: '',
      formData: {
        titleImage: '',
        posterImage: '',
        portraitImage: '',
        isInited: false,
      },
    };
  },
  computed: {
    ...mapState({
      folderLanguage: (state) => state.localization.folderLanguage,
      isEditedForm: (state) => state.localization.isEditedForm,
      isLoading: (state) => state.localization.isLoading,
    }),
    ...mapGetters([
      'getCurrentAccountId',
    ]),
    imageSubmitParams() {
      return {
        accountId: this.getCurrentAccountId,
        id: this.type === 'season' ? this.$route.params.seasonId : this.$route.params.id,
        type: this.type,
        langId: this.lang.id,
        data: {},
      };
    },
    isNeedTitleImg() {
      return this.type !== 'season';
    },
  },
  watch: {
    itemsWithChanges: {
      handler(newItems) {
        if (newItems.length) {
          this.setHasChanges(true);
        } else {
          this.setHasChanges(false);
        }
      },
      deep: true,
    },
    metadata: {
      handler() {
        this.initFormData();
      },
      deep: true,
    },
  },
  created() {
    const value = this.enabledLanguages.map(((item) => ({
      id: item.id,
      value: false,
    })));
    this.$store.dispatch(INIT_FORM_CHANGES, value);

    this.loadData();
  },
  beforeDestroy() {
    this.$store.dispatch(SET_FORM_CHANGES, []);
  },
  methods: {
    loadData() {
      const params = {
        accountId: this.getCurrentAccountId,
        id: this.type === 'season' ? this.$route.params.seasonId : this.$route.params.id,
        type: this.type,
        langId: this.lang.id,
      };
      this.$store.dispatch(GET_LANGUAGE_FOLDER_REQUEST, params).then(() => {
        this.initFormData();
      });
    },
    initFormData() {
      this.formData.titleImage = this.folderLanguage.titleImage
        ? `${this.folderLanguage.titleImage}?height=220`
        : '';
      this.formData.posterImage = this.folderLanguage.posterImage
        ? `${this.folderLanguage.posterImage}?height=360`
        : '';
      this.formData.portraitImage = this.folderLanguage.portraitImage
        ? `${this.folderLanguage.portraitImage}?height=360`
        : '';

      this.formData.isInited = true;
    },
    getImageSource(imageType) {
      const imageProperty = imageType.split('-')[1];
      return {
        url: this.formData[imageProperty],
        name: imageType,
        canEdit: true,
        canDelete: true,
        imageType,
        imageProperty,
        id: imageType,
      };
    },
    handleAddItemWithChanges(id) {
      if (!this.itemsWithChanges.includes(id)) {
        this.itemsWithChanges.push(id);
      }
    },
    handleRemoveItemWithChanges(id) {
      const index = this.itemsWithChanges.indexOf(id);
      if (index !== -1) {
        this.itemsWithChanges.splice(index, 1);
      }
    },
    handleItemInProgress(item) {
      console.log('Item is in progress', item);
    },
    handleReloadItem() {
      this.loadData();
    },
    onSubmitSuccess() {
      // this.isPosterSmallChanged = false;
      // this.isPortraitImageChanged = false;
      // this.$emit('changedLanguage', { id: this.lang.id, value: false });
      // this.$store.dispatch(SET_FORM_CHANGES, { id: this.lang.id, value: false });
      this.$toasted.global.success({
        message: this.successMessages,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.translate-wrapper {
  padding-left: 20px;
  padding-right: 428px;
}

.wrapper-small {
  margin-bottom: 20px;
}

.warning-message {
  color: #F2C94C;
  gap: 6px;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid #F2C94C;
  border-radius: 4px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  width: fit-content;
  margin-bottom: 30px;
}

@media all and (max-width: 1670px) {
  .translate-wrapper {
    padding-right: 300px;
  }
}

@media all and (max-width: 1460px) {
  .translate-wrapper {
    padding-right: 200px;
  }
}

@media all and (max-width: 1300px) {
  .translate-wrapper {
    padding-right: 100px;
  }
}
</style>
